import React, { useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import { useToast } from '../../hooks/useToast';

const toastConfig = {
  success: {
    icon: CheckCircle,
    barClass: 'bg-green-500',
    iconClass: 'text-green-600',
  },
  error: {
    icon: AlertCircle,
    barClass: 'bg-red-500',
    iconClass: 'text-red-600',
  },
  info: {
    icon: Info,
    barClass: 'bg-blue-500',
    iconClass: 'text-blue-600',
  },
  warning: {
    icon: AlertCircle,
    barClass: 'bg-yellow-500',
    iconClass: 'text-yellow-600',
  },
};

const Toast = ({ message, type }) => {
  const { hideToast } = useToast();
  
  // Fallback to 'info' if type is not recognized
  const config = toastConfig[type] || toastConfig.info;
  const { icon: Icon, barClass, iconClass } = config;

  useEffect(() => {
    const timer = setTimeout(() => {
      hideToast();
    }, 5000); // Auto-dismiss after 5 seconds

    return () => clearTimeout(timer);
  }, [message, type, hideToast]);

  return (
    <div className="max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden animate-fade-in-up">
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Icon className={`h-6 w-6 ${iconClass}`} aria-hidden="true" />
          </div>
          <div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium text-gray-900">{message}</p>
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              onClick={hideToast}
              className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <span className="sr-only">Close</span>
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      <div className={`h-1 ${barClass}`}></div>
    </div>
  );
};

const ToastContainer = ({ toast }) => {
  if (!toast) return null;

  return (
    <div
      aria-live="assertive"
      className="fixed inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start z-50"
    >
      <div className="w-full flex flex-col items-center space-y-4 sm:items-end">
        <Toast message={toast.message} type={toast.type} />
      </div>
    </div>
  );
};

export default ToastContainer;